import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import toast from 'react-hot-toast';

import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import red from '@mui/material/colors/red';
import { Paper } from '@mui/material/';

import {
  signupVerifyOTP,
  signupMobileVerifyOTP,
  resendSignupOTP,
  resendSignupMobileOTP,
  resetErrorsDispatch,
} from '../../store/actions/register';
import { logoutUser } from '../../store/actions/auth';

import { ColoredButton } from '../../components/Buttons/colored';
import * as ROUTES from '../../constants/routes';
import { useTheme } from '@mui/material/styles';

const ALLOWEDRETRIES = 4;
const ALLOWEDMOBILERETRIES = 2;

const SignUpOTPVerify = (props) => {
  const theme = useTheme();

  const classes = {
    root: {
      height: 'calc(100vh - 2rem)',
    },
    formWrapper: {
      width: '100%',
      maxWidth: '800px',
      height: 'max-content',
      margin: '80px 16px 16px',
      padding: '24px',
      boxShadow: '0px 10px 16px #d3cff650',
      border: '1px solid #1f1f3320',
      [theme.breakpoints.down('xs')]: {
        margin: '80px 0 0',
        border: 'none',
        boxShadow: 'none',
        borderRadius: '0',
      },
    },
    formTitle: {
      color: '#4e4e7e',
      fontWeight: '600',
      lineHeight: '1.2',
      fontSize: '24px',
      marginBottom: '24px',
      [theme.breakpoints.down('xs')]: {
        fontSize: '24px',
      },
    },
    resendOTP: {
      fontWeight: '600',
      color: '#7E87F1',
      '&:hover': {
        textDecoration: 'none',
      },
      paddingLeft: '5px',
    },
  };

  useEffect(() => {
    if (!props.email && !props.mobile && !props.info)
      props.history.push(ROUTES.SIGN_UP);
  }, [props.email, props.mobile, props.info]);

  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState('');
  const [remainingTries, setRemainingTries] = useState(
    props.email ? ALLOWEDRETRIES : ALLOWEDMOBILERETRIES,
  );

  const onSubmit = (event) => {
    event.preventDefault();
    setLoading(true);
    if (props.email && remainingTries > 0) {
      props.signupVerifyOTP(props.email, otp);
    } else if (props.mobile && remainingTries > 0) {
      props.signupMobileVerifyOTP(props.mobile, otp);
    } else {
      toast.error('No more retries allowed. Reset registration.');
    }
  };

  useEffect(() => {
    if (props.signupError && typeof props.signupError === 'string') {
      setLoading(false);
      toast.error(props.signupError);
      let old = remainingTries;
      setRemainingTries(--old);
      props.resetErrorsDispatch();
    } else if (props.accessToken) {
      setLoading(false);
      props.history.push(ROUTES.SIGN_UP_ONBOARD_LANDING);
    }
  }, [props.signupError, props.accessToken]);

  const isInvalid = otp === '';

  return (
    <Box display="flex" justifyContent="center" sx={classes.root}>
      <Paper elevation={0} sx={classes.formWrapper}>
        <form onSubmit={onSubmit}>
          <Grid spacing={1} direction="row" container>
            <Grid item xs={12}>
              <Typography gutterBottom sx={classes.formTitle}>
                {props.email
                  ? `You must have received an activation code on ${props.email}, do check in spam too`
                  : `You must have received an activation code on ${props.mobile}`}
              </Typography>
              <Typography
                variant="p"
                display="block"
                gutterBottom
                style={{ color: '#666' }}
              >
                {'We will next be asking you to complete your basic profile'}
              </Typography>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                {`You have ${remainingTries} retries left`}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControl variant="outlined">
                <TextField
                  label={'Activation Code'}
                  id="otp"
                  name="otp"
                  value={otp}
                  variant="outlined"
                  onChange={(e) => setOtp(e.target.value.trim())}
                  type="tel"
                  placeholder={'Activation Code'}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <ColoredButton
                loading={loading}
                disabled={isInvalid}
                type="submit"
                variant="contained"
                bgcolor={red}
                disableElevation
              >
                {'Verify'}
              </ColoredButton>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" display="block" mb={2} gutterBottom>
                {"Didn't receive the activation code yet?"}
                <Link
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    props.email
                      ? props.resendSignupOTP(props.email)
                      : props.resendSignupMobileOTP(props.mobile);
                  }}
                  sx={classes.resendOTP}
                >
                  {'Resend activation code'}
                </Link>
              </Typography>
              <Typography variant="body2" display="block" gutterBottom>
                {'Want to restart registration with a different email/mobile?'}
                <Link
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    props.logoutUser();
                  }}
                  sx={classes.resendOTP}
                >
                  {'Reset registration'}
                </Link>
              </Typography>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  info: state.user.info,
  email: state.register.email,
  mobile: state.register.mobile,
  signupError: state.register.errors,
  accessToken: state.auth.accessToken,
});

const mapDispatchToProps = {
  signupVerifyOTP,
  signupMobileVerifyOTP,
  resendSignupOTP,
  resendSignupMobileOTP,
  resetErrorsDispatch,
  logoutUser,
};

const SignUpOTPVerifyForm = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(SignUpOTPVerify),
);

export default SignUpOTPVerifyForm;
